<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-semibold">Perfis de Acesso</h1>
      <div class="flex gap-2">
        <Button
          label="Excluir Perfis Default"
          icon="pi pi-trash"
          class="p-button-text p-button-rounded p-button-danger"
          @click="handleDeleteDefaultProfiles"
        />
        <Button label="Novo Perfil" icon="pi pi-plus" @click="handleNewProfile" />
      </div>
    </div>

    <DataTable :value="profileStore.profiles" :loading="profileStore.loading" class="p-datatable-sm" stripedRows>
      <!-- Colunas existentes -->
      <Column field="name" header="Nome">
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <i :class="['pi text-lg', data.icon]" :style="{ color: data.color }"></i>
            <span>{{ data.name }}</span>
          </div>
        </template>
      </Column>
      
      <Column field="description" header="Descrição" />
      
      <!-- Nova coluna de permissões -->
      <Column field="menuPermissions" header="Permissões">
        <template #body="{ data }">
          <div class="space-y-2">
            <!-- Menu Principal -->
            <div v-if="getMenuPermissions(data.menuPermissions, menuItems).length > 0">
              <div class="text-sm font-medium text-gray-700">Menu Principal:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="item in getMenuPermissions(data.menuPermissions, menuItems)" 
                  :key="item.path"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100"
                >
                  <i :class="['pi text-sm', item.icon]"></i>
                  {{ item.name }}
                </span>
              </div>
            </div>

            <!-- Configurações da Organização -->
            <div v-if="getMenuPermissions(data.menuPermissions, organizationSettings).length > 0">
              <div class="text-sm font-medium text-gray-700">Configurações da Organização:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="item in getMenuPermissions(data.menuPermissions, organizationSettings)" 
                  :key="item.path"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100"
                >
                  <i :class="['pi text-sm', item.icon]"></i>
                  {{ item.name }}
                </span>
              </div>
            </div>

            <!-- Configurações Gerais -->
            <div v-if="getMenuPermissions(data.menuPermissions, generalSettings).length > 0">
              <div class="text-sm font-medium text-gray-700">Configurações Gerais:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="item in getMenuPermissions(data.menuPermissions, generalSettings)" 
                  :key="item.path"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100"
                >
                  <i :class="['pi text-sm', item.icon]"></i>
                  {{ item.name }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Column>

      <Column :exportable="false" style="width: 100px">
        <template #body="{ data }">
          <div class="flex gap-2">
            <Button
              icon="pi pi-pencil"
              class="p-button-text p-button-rounded"
              @click="handleEdit(data)"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-text p-button-rounded p-button-danger"
              @click="confirmDelete(data.id!)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- New Profile Modal -->
    <NewProfileModal
      :show="showNewProfileModal"
      @close="showNewProfileModal = false"
    />

    <!-- Edit Profile Modal -->
    <EditProfileModal
      v-if="selectedProfile"
      :show="showEditProfileModal"
      :profile="selectedProfile"
      @close="() => {
        showEditProfileModal = false
        selectedProfile = null
      }"
      @saved="() => {
        showEditProfileModal = false
        selectedProfile = null
        profileStore.fetchProfiles()
      }"
    />

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog
      :show="showConfirmDelete"
      title="Confirmar Exclusão"
      message="Tem certeza que deseja excluir este perfil?"
      @confirm="handleDelete"
      @cancel="showConfirmDelete = false"
    />
    <Toast />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useProfileStore } from '@/stores/profile'
import type { Profile } from '@/firebase/services/profiles.service'
import NewProfileModal from '@/components/profile/NewProfileModal.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import EditProfileModal from '@/components/profile/EditProfileModal.vue'
import { deleteDefaultProfiles } from '@/firebase/services/profiles.service'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const profileStore = useProfileStore()
const showNewProfileModal = ref(false)
const showConfirmDelete = ref(false)
const profileToDelete = ref<string | null>(null)
const showEditProfileModal = ref(false)
const selectedProfile = ref<Profile | null>(null)
const isLoading = ref(false)

const toast = useToast()

onMounted(() => {
  profileStore.fetchProfiles()
})

const confirmDelete = (id: string) => {
  profileToDelete.value = id
  showConfirmDelete.value = true
}

const handleDelete = async () => {
  if (profileToDelete.value) {
    await profileStore.deleteProfile(profileToDelete.value)
    showConfirmDelete.value = false
    profileToDelete.value = null
  }
}

const handleEdit = (profile: Profile) => {
  selectedProfile.value = profile
  showEditProfileModal.value = true
}

const handleNewProfile = () => {
  showNewProfileModal.value = true
}

const handleDeleteDefaultProfiles = async () => {
  try {
    isLoading.value = true;
    const count = await deleteDefaultProfiles();
    toast.add({severity:'success', summary: 'Sucesso', detail: `${count} perfis default foram excluídos com sucesso!`, life: 3000});
    await profileStore.fetchProfiles(); // Recarregar lista de perfis
  } catch (error) {
    console.error('[ProfileManagement] Erro ao excluir perfis default:', error);
    toast.add({severity:'error', summary: 'Erro', detail: 'Erro ao excluir perfis default: ' + error.message, life: 3000});
  } finally {
    isLoading.value = false;
  }
}

// Menu items
const menuItems = [
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    icon: 'pi-home', 
    description: 'Painel principal com visão geral do sistema' 
  },
  { 
    path: '/accounts', 
    name: 'Contas a Pagar e Receber', 
    icon: 'pi-money-bill', 
    description: 'Gerenciamento de contas e transações' 
  },
  { 
    path: '/cash-flow', 
    name: 'Fluxo de Caixa', 
    icon: 'pi-chart-line', 
    description: 'Relatórios e análises financeiras' 
  },
  { 
    path: '/banks', 
    name: 'Bancos', 
    icon: 'pi-building', 
    description: 'Configurações de bancos' 
  },
  { 
    path: '/suppliers', 
    name: 'Fornecedores', 
    icon: 'pi-briefcase', 
    description: 'Gerenciamento de fornecedores' 
  },
  { 
    path: '/profiles', 
    name: 'Perfis de Acesso', 
    icon: 'pi-users', 
    description: 'Gerenciamento de perfis de acesso' 
  },
  { 
    path: '/my-account', 
    name: 'Minha Conta', 
    icon: 'pi-user', 
    description: 'Configurações da conta' 
  },
  {
    path: 'https://bhissdigital.pbh.gov.br/nfse/',
    name: 'Prefeitura de BH',
    icon: 'pi-external-link',
    description: 'Portal de Notas Fiscais da Prefeitura de BH',
    external: true
  },
  {
    path: 'https://inter.co/#',
    name: 'Banco Inter',
    icon: 'pi-credit-card',
    description: 'Internet Banking do Banco Inter',
    external: true
  }
]

// Configurações da Organização
const organizationSettings = [
  { 
    path: '/banks', 
    name: 'Bancos', 
    icon: 'pi-building', 
    description: 'Configurações de bancos' 
  },
  { 
    path: '/suppliers', 
    name: 'Fornecedores', 
    icon: 'pi-briefcase', 
    description: 'Gerenciamento de fornecedores' 
  },
  { 
    path: '/customers', 
    name: 'Clientes', 
    icon: 'pi-users', 
    description: 'Gerenciamento de clientes' 
  },
  { 
    path: '/cost-centers', 
    name: 'Centro de Custos', 
    icon: 'pi-chart-pie', 
    description: 'Gerenciamento de centro de custos' 
  },
  { 
    path: '/payment-status', 
    name: 'Status de Pagamento', 
    icon: 'pi-tag', 
    description: 'Configuração de status de pagamento' 
  }
]

// Configurações Gerais
const generalSettings = [
  { 
    path: '/profiles', 
    name: 'Perfis de Acesso', 
    icon: 'pi-users', 
    description: 'Gerenciamento de perfis de acesso' 
  },
  { 
    path: '/my-account', 
    name: 'Minha Conta', 
    icon: 'pi-user', 
    description: 'Configurações da conta' 
  },
  { 
    path: '/settings', 
    name: 'Configurações', 
    icon: 'pi-cog', 
    description: 'Configurações gerais do sistema' 
  }
]

// Função para filtrar as permissões ativas
const getMenuPermissions = (permissions: { [key: string]: boolean }, items: any[]) => {
  if (!permissions) return []
  return items.filter(item => {
    const permissionKey = item.external ? item.name : item.path
    return permissions[permissionKey]
  })
}
</script>